import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { Skill } from './useSkills';

export interface UserProfile {
  id: string;
  name: string;
  bio: string | null;
  avatar_url: string | null;
  renaissance_score: number;
  level: string;
  rating: number;
  sessions_completed: number;
  is_available: boolean;
  teaching_skills: Skill[];
  learning_skills: Skill[];
  gender: string | null;
  location: string | null;
  interested_in: string[] | null;
  here_for: string[] | null;
  show_preferences: boolean;
  updated_at: string | null;
  created_at: string | null;
}

export const useUsers = (availableOnly: boolean = false) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let query = supabase
          .from('profiles')
          .select('*');

        if (availableOnly) {
          query = query.eq('is_available', true);
        }

        // Exclude current user
        if (currentUser) {
          query = query.neq('id', currentUser.id);
        }

        const { data: profilesData, error: profilesError } = await query;

        if (profilesError) throw profilesError;

        // Fetch skills for each user
        const usersWithSkills = await Promise.all(
          (profilesData || []).map(async (profile) => {
            // Fetch teaching skills
            const { data: teachingData } = await supabase
              .from('user_skills')
              .select('skill:skills(*)')
              .eq('user_id', profile.id);

            // Fetch learning skills
            const { data: learningData } = await supabase
              .from('user_learning')
              .select('skill:skills(*)')
              .eq('user_id', profile.id);

            return {
              ...profile,
              teaching_skills: teachingData?.map((item: any) => item.skill) || [],
              learning_skills: learningData?.map((item: any) => item.skill) || [],
            };
          })
        );

        setUsers(usersWithSkills);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();

    // Set up realtime subscription for availability changes
    const channel = supabase
      .channel('users-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
        },
        () => {
          fetchUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser, availableOnly]);

  return {
    users,
    loading,
    error,
  };
};
